/**
 * 生成設定の localStorage 永続化キーと初期値解決。
 *
 * useGenerationState.tsx から切り出したもの (ロジック変更なし)。
 * Context 分割 (SettingsProvider) と後方互換ファサードの双方から参照するため、
 * 循環インポートを避けて独立モジュールに置く。
 */

import { DEFAULT_GENERATION_MODEL, resolveLegacyImageModel } from './modelCatalog';
import type { GenerationSettings } from './types';

export const STORAGE_KEY_GENERATION_MODEL = 'generation_model';
export const STORAGE_KEY_ASPECT_RATIO = 'aspect_ratio';
export const STORAGE_KEY_EXCLUDE_TEXT = 'exclude_text_response';
// 「AIに送る情報」トグル (ロゴ / 商品情報 / ブランド情報)。既定は全て OFF
export const STORAGE_KEY_INCLUDE_LOGO = 'include_logo';
export const STORAGE_KEY_INCLUDE_PRODUCT_INFO = 'include_product_info';
export const STORAGE_KEY_INCLUDE_BRAND_INFO = 'include_brand_info';
// 「案件情報からのテキスト生成」で利用者が最後に選んだ3択 ('generate' | 'none' | 'never')。
// 未保存 (一度も選んでいない) なら、案件に結びつく素材があるとき「生成する」を既定にする
export const STORAGE_KEY_GENERATE_TEXT_FROM_EC = 'generate_text_from_ec';
export const STORAGE_KEY_VIDEO_GENERATION_MODE = 'video_generation_mode';
export const STORAGE_KEY_VISUAL_STYLE = 'visual_style';
export const STORAGE_KEY_SHOOTING_METHOD = 'shooting_method';
export const STORAGE_KEY_DIALECT = 'scenario_dialect';
// シナリオ全体の「モーショングラフィックス」3択 ('' | 'scenes' | 'whole'・計画 md §10.14)
export const STORAGE_KEY_MOTION_GRAPHICS = 'scenario_motion_graphics';
// シナリオ全体のターゲット性別・年代 (属性ツリー target_gender / target_age の option key・'' = 指定なし)
export const STORAGE_KEY_TARGET_GENDER = 'scenario_target_gender';
export const STORAGE_KEY_TARGET_AGE = 'scenario_target_age';
// 媒体要件の最後の選択。menu は platform に、spec は menu に紐づくため、
// 親→子のマップで保持する。これにより複数のプラットフォーム / メニューを
// 行き来しても、それぞれの親で最後に選んだ子を覚えていられる。
export const STORAGE_KEY_MEDIA_PLATFORM = 'media_platform_id';
export const STORAGE_KEY_MEDIA_LAST_MENU_BY_PLATFORM = 'media_last_menu_by_platform';
export const STORAGE_KEY_MEDIA_LAST_SPEC_BY_MENU = 'media_last_spec_by_menu';

export function loadStringMap(key: string): Record<string, string> {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const out: Record<string, string> = {};
      for (const [k, v] of Object.entries(parsed)) {
        if (typeof v === 'string') out[k] = v;
      }
      return out;
    }
  } catch {
    // パースエラーは無視
  }
  return {};
}

export function saveStringMap(key: string, map: Record<string, string>): void {
  try {
    localStorage.setItem(key, JSON.stringify(map));
  } catch {
    // 書き込みエラーは無視 (容量超過等)
  }
}

// 旧スキーマ (media_menu_id / media_spec_id を単一値で保存) からの一回限りの移行。
// 旧 platform に紐づく形で map に転記し、旧キーを削除する。
function migrateLegacyMediaSelectionKeys(): void {
  const legacyMenu = localStorage.getItem('media_menu_id');
  const legacySpec = localStorage.getItem('media_spec_id');
  if (!legacyMenu && !legacySpec) return;

  const platform = localStorage.getItem(STORAGE_KEY_MEDIA_PLATFORM);
  if (legacyMenu && platform) {
    const map = loadStringMap(STORAGE_KEY_MEDIA_LAST_MENU_BY_PLATFORM);
    if (!map[platform]) {
      map[platform] = legacyMenu;
      saveStringMap(STORAGE_KEY_MEDIA_LAST_MENU_BY_PLATFORM, map);
    }
  }
  if (legacySpec && legacyMenu) {
    const map = loadStringMap(STORAGE_KEY_MEDIA_LAST_SPEC_BY_MENU);
    if (!map[legacyMenu]) {
      map[legacyMenu] = legacySpec;
      saveStringMap(STORAGE_KEY_MEDIA_LAST_SPEC_BY_MENU, map);
    }
  }
  localStorage.removeItem('media_menu_id');
  localStorage.removeItem('media_spec_id');
}

/** 媒体要件: 指定プラットフォームで最後に選択されていたメニュー ID。 */
export function getLastMenuForPlatform(platformId: string): string | undefined {
  return loadStringMap(STORAGE_KEY_MEDIA_LAST_MENU_BY_PLATFORM)[platformId];
}

/** 媒体要件: 指定メニューで最後に選択されていた画像仕様 ID。 */
export function getLastSpecForMenu(menuId: string): string | undefined {
  return loadStringMap(STORAGE_KEY_MEDIA_LAST_SPEC_BY_MENU)[menuId];
}

export type GenerateTextPreference = 'generate' | 'none' | 'never';

/** 「案件情報からのテキスト生成」の保存済みの選択。未保存・不正値は null。 */
export function loadGenerateTextPreference(): GenerateTextPreference | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_GENERATE_TEXT_FROM_EC);
    return raw === 'generate' || raw === 'none' || raw === 'never' ? raw : null;
  } catch {
    return null;
  }
}

/** 利用者がボタンで選んだときだけ呼ぶ (自動で既定を当てたときは保存しない)。 */
export function saveGenerateTextPreference(pref: GenerateTextPreference): void {
  try {
    localStorage.setItem(STORAGE_KEY_GENERATE_TEXT_FROM_EC, pref);
  } catch {
    // 書き込みエラーは無視 (容量超過等)
  }
}

/**
 * 保存済みの選択から初期の attributes.generate_text_from_ec を決める。
 * 「生成する」/未保存は undefined (=おまかせ) にしておき、素材の案件紐づきが分かった時点で
 * GenerationWorkspace の同期エフェクトが true に上げる (案件が無ければ指定なしのまま)。
 */
function initialGenerateTextFromEc(): false | 'never' | undefined {
  const pref = loadGenerateTextPreference();
  if (pref === 'never') return 'never';
  if (pref === 'none') return false;
  return undefined;
}

export const DEFAULT_SETTINGS: GenerationSettings = {
  // 人物属性 (gender/ethnicity/age) は attributes.person 配下が唯一の正本。
  // かつて attributes 直下と settings 直下にもフラットで重複定義されていたが、
  // バックエンドの _nest_flat_selections がフラットキーを person 配下へ埋め戻すため、
  // AI俳優選択時に既定の age=30 / ethnicity='jp' がプロンプト混入する事故があった
  // (2026-07-31)。フラットキーを再追加しないこと。
  attributes: {
    background: 'none',
    scene: 'none',
    seasons: 'none',
    overall_impression: 'none',
    hand: 'none',
    orientation: 'none',
    effect: 'none',
    aspect_ratio: 'none',
    person: {
      gender: 'none',
      ethnicity: 'jp',
      age: null,
      fashion: 'none',
      shot: 'none',
    },
  },
  background: 'none',
  hand: 'none',
  orientation: 'none',
  effect: 'none',
  aspect_ratio: 'none',
  modelMeta: null,
  media_platform_id: null,
  media_menu_id: null,
  media_spec_id: null,
  media_guidelines: [],
  media_menu_guidelines: [],
};

/** LocalStorage から画像生成モデル・アスペクト比・媒体要件を読み込んだ初期 settings。 */
export function getInitialSettings(): GenerationSettings {
  // 旧スキーマからの一回限りの移行 (idempotent)
  migrateLegacyMediaSelectionKeys();

  // 廃止済みモデル (gpt-image-2-*) の旧値は同品質の現行コードへ読み替える
  const savedModel = resolveLegacyImageModel(localStorage.getItem(STORAGE_KEY_GENERATION_MODEL));
  const savedAspectRatio = localStorage.getItem(STORAGE_KEY_ASPECT_RATIO);
  const savedExcludeText = localStorage.getItem(STORAGE_KEY_EXCLUDE_TEXT);
  const savedIncludeLogo = localStorage.getItem(STORAGE_KEY_INCLUDE_LOGO);
  const savedIncludeProductInfo = localStorage.getItem(STORAGE_KEY_INCLUDE_PRODUCT_INFO);
  const savedIncludeBrandInfo = localStorage.getItem(STORAGE_KEY_INCLUDE_BRAND_INFO);
  const savedPlatform = localStorage.getItem(STORAGE_KEY_MEDIA_PLATFORM);
  const initialGenerateText = initialGenerateTextFromEc();
  const initialMenu = savedPlatform ? getLastMenuForPlatform(savedPlatform) ?? null : null;
  const initialSpec = initialMenu ? getLastSpecForMenu(initialMenu) ?? null : null;
  return {
    ...DEFAULT_SETTINGS,
    generation_model: savedModel || DEFAULT_GENERATION_MODEL,
    aspect_ratio: savedAspectRatio || 'none',
    exclude_text_response: savedExcludeText === 'true',
    include_logo: savedIncludeLogo === 'true',
    include_product_info: savedIncludeProductInfo === 'true',
    include_brand_info: savedIncludeBrandInfo === 'true',
    media_platform_id: savedPlatform || null,
    media_menu_id: initialMenu,
    media_spec_id: initialSpec,
    attributes: {
      ...DEFAULT_SETTINGS.attributes,
      aspect_ratio: savedAspectRatio || 'none',
      ...(initialGenerateText === undefined ? {} : { generate_text_from_ec: initialGenerateText }),
    },
  };
}
